import HistoryIcon from "@mui/icons-material/History";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useEffect, useRef, type MouseEvent } from "react";
import { useProposalFieldHistoryPopoverTrigger } from "work/proposals/negotiation/use-proposal-field-history-popover-trigger";
import { useSession } from "users/__session/session-context";
import { useMarkProposalChangesSeenMutation } from "work/proposals/negotiation/proposal-collaboration-api";
import {
  ProposalFieldChangeBadgeBubbles,
  useProposalFieldChangeBadgeState,
} from "work/proposals/negotiation/proposal-field-change-badge";
import {
  proposalTimelineItemPathForField,
  proposalTimelinePathForField,
} from "work/proposals/negotiation/proposal-field-timeline-path";
import ProposalTimelinePanel from "work/proposals/negotiation/proposal-timeline-panel";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const HistoryPanelContainer = styled(Box)(({ theme }) => ({
  maxHeight: 420,
  maxWidth: 440,
  minWidth: 300,
  overflowY: "auto",
  padding: theme.spacing(1.5),
}));

const HistoryIconContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  position: "relative",
}));

type ProposalFieldHistoryButtonProps = {
  field: ProposalField;
  negotiationId?: string;
  viewerScope?: string;
  /**
   * Narrows the panel to one item of a collection field, by its identity in the negotiation stream —
   * see `ProposalTimelinePanelProps.collectionItemId`. A fee-schedule category row passes its category
   * id, so the row's history is that category's alone rather than the whole schedule's (#4951).
   */
  collectionItemId?: string;
  /** Defaults to the signed-in user; the badge never counts the viewer's own changes as unread. */
  currentUserId?: string;
  toolTip?: string;
  disabled?: boolean;
  /**
   * Suppresses this button's own change-badge bubbles (both the unread count and the collision "!") —
   * opening the popover still marks the field seen. Set by callers that already place a
   * {@link ProposalFieldChangeBadge} directly on the field's input — the active Details-tab
   * Name/Description/date fields, whose `TextRedlineInput`/`DateRedlinePicker` embed this button beside
   * their comment icon — so the same signal is never shown twice on one field; it just moves to the
   * input the button sits beside.
   */
  hideBadge?: boolean;
};

/**
 * Inline per-field change history, in the field's own action row. Reuses the read-only
 * ProposalTimelinePanel (which already renders scalar and collection history and already powers the
 * comments drawer's Changes tab) scoped to one field, so no new data plumbing or backend work is needed.
 *
 * Renders nothing until the negotiation stream exists (drafts before their first save, or the legacy
 * path). Keeping every Redux/session hook inside the inner component — mounted only once negotiationId
 * and viewerScope are present — lets the many call sites that render this without a store (and without a
 * negotiation) keep working untouched.
 *
 * The panel is a `Popper`, not a `Popover`, because it must never seize the page. The change traverser
 * can open this panel without a click (see `proposal-field-history-request-context.ts`), and a
 * `Popover` is a `Modal`: MUI's modal manager marks every sibling of the modal root `aria-hidden`, so
 * an unprompted stop took the whole proposal — the field being read, and the Prev/Next buttons that
 * opened it — out of the accessibility tree until someone dismissed it. That is what the persistent
 * comments drawer this stop replaced never did. A `Popper` with a `ClickAwayListener` dismisses the
 * same way without hiding anything, so Next stays pressable underneath and the field stays editable.
 */
export default function ProposalFieldHistoryButton(props: Readonly<ProposalFieldHistoryButtonProps>) {
  const { field, negotiationId, viewerScope, collectionItemId, currentUserId, disabled, hideBadge } = props;
  const toolTip = props.toolTip ?? "View change history";

  if (!negotiationId || !viewerScope) return null;

  return (
    <ConnectedProposalFieldHistoryButton
      field={field}
      negotiationId={negotiationId}
      viewerScope={viewerScope}
      collectionItemId={collectionItemId}
      currentUserId={currentUserId}
      toolTip={toolTip}
      disabled={disabled}
      hideBadge={hideBadge}
    />
  );
}

type ConnectedProps = Omit<ProposalFieldHistoryButtonProps, "negotiationId" | "viewerScope"> & {
  negotiationId: string;
  viewerScope: string;
  toolTip: string;
};

function ConnectedProposalFieldHistoryButton({
  field,
  negotiationId,
  viewerScope,
  collectionItemId,
  currentUserId: explicitCurrentUserId,
  toolTip,
  disabled,
  hideBadge,
}: Readonly<ConnectedProps>) {
  // Shared with the timeline dropdown's trigger: the guard against a composite field control's
  // ancestor click handler tearing the row down on the same click that opened the popover, and the
  // page's standing request for this field's history (the change traverser's stop).
  const { anchorEl, isOpen, open, close, triggerRef } = useProposalFieldHistoryPopoverTrigger(field);

  const session = useSession();
  const currentUserId = explicitCurrentUserId ?? session.user?.id?.value;
  const [markProposalChangesSeen] = useMarkProposalChangesSeenMutation();

  const badgeState = useProposalFieldChangeBadgeState(negotiationId, field, currentUserId);
  const { unreadCount, latestUnseenSequence, badgeLabel, hasPendingChange } = badgeState;

  function handleOpen(event: MouseEvent<HTMLElement>) {
    open(event);

    // Marking seen on open is the analog of markFieldCommentsAsRead firing when a comment thread opens:
    // advance the field's cursor past the changes just surfaced so the badge (and any collision on this
    // field the viewer has now acknowledged) clears. Nothing to clear when nothing is unread — skip the
    // network call.
    // A fee-schedule category acknowledges itself and not its siblings: what was surfaced is this
    // category's history, so the cursor that moves is the category's own (#5118).
    const path = proposalTimelineItemPathForField(field) ?? proposalTimelinePathForField(field);
    if (path && unreadCount > 0) {
      void markProposalChangesSeen({
        negotiationId,
        path,
        throughStreamVersion: latestUnseenSequence,
      });
    }
  }

  // Escape closes it. A `Popover` got this from `Modal`, which also took focus; this panel
  // deliberately leaves focus where the reader had it, so the key never reaches the panel itself and
  // the document is the only place that hears it.
  //
  // `close` is read through a ref because the page rebuilds the history request it closes over on every
  // render: naming it as a dependency would tear the listener down and rebuild it just as often, while
  // `isOpen` is what actually decides whether anything should be listening.
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });
  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") closeRef.current();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  return (
    <>
      <Tooltip title={toolTip}>
        <span>
          <IconButton
            ref={triggerRef}
            size="medium"
            disabled={disabled}
            aria-label={badgeLabel ? `${toolTip} (${badgeLabel})` : toolTip}
            onClick={handleOpen}
          >
            <HistoryIconContainer>
              {!hideBadge && <ProposalFieldChangeBadgeBubbles state={badgeState} />}
              <HistoryIcon
                color={badgeState.isCollision ? "error" : hasPendingChange ? "info" : "inherit"}
                fontSize="medium"
              />
            </HistoryIconContainer>
          </IconButton>
        </span>
      </Tooltip>
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-end"
        // `Popper` names its own root `role="tooltip"`, which this is not: the panel it carries states
        // its own `region`, and a tooltip wrapper around that only misreports it.
        role="presentation"
        // Above the page's own drawers and sticky headers; a `Popover` used to get this for free.
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={close}>
          <Paper elevation={8}>
            <HistoryPanelContainer>
              {/* Only fetch while the panel is open; comments stay in the dedicated comments surface. */}
              <ProposalTimelinePanel
                negotiationId={negotiationId}
                viewerScope={viewerScope}
                field={field}
                collectionItemId={collectionItemId}
                enabled={isOpen}
                includeComments={false}
                surface="inline"
              />
            </HistoryPanelContainer>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
